import React from "react";
import { Link } from "react-router-dom";
import { Star, MapPin, Users, Fence, Droplet, Dog, Info } from "lucide-react";
import { getCategory } from "../lib/categories";
import { markPhotoLoaded } from "../lib/photoCache";
import { placeUrl } from "../lib/placeUrl";
import { api } from "../lib/api";
import CommunityBadge from "./CommunityBadge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const photoUrl = (ref, w = 400) => {
  if (!ref) return null;
  if (ref.startsWith("http") || ref.startsWith("/")) return ref;
  return `${BACKEND_URL}/api/photo?ref=${encodeURIComponent(ref)}&w=${w}`;
};

const AMENITY_ICON = {
  "Off-leash": Dog,
  "Fenced": Fence,
  "Water station": Droplet,
  "Small dog section": Dog,
};

const AMENITY_LABEL = {
  "Off-leash": "Off-leash",
  "Fenced": "Fenced",
  "Water station": "Water station",
  "Small dog section": "Small dogs",
};

// Returns:
//   true  → open right now (real parseable hours, current time in range)
//   false → closed right now (real parseable hours, current time out of range)
//   null  → unknown / unparseable / missing hours. Callers MUST render
//           no "Open now"/"Closed" pill in this state, because defaulting
//           to true was the 9/6/2026 bug: any listing without walk-in
//           hours (service-area businesses, "By appointment" strings)
//           read as "Open now" 24/7. The old truthy defaults are gone.
const isOpenNow = (hoursStr) => {
  if (!hoursStr) return null;                   // missing (was: true)
  if (/closed/i.test(hoursStr)) return false;
  if (/dawn|dusk|sunrise|sunset|24/i.test(hoursStr)) return true;
  // Format like "7am–10pm"
  const m = hoursStr.replace(/\s/g, "").match(/(\d{1,2})(am|pm)[–-](\d{1,2})(am|pm)/i);
  if (!m) return null;                          // unparseable (was: true) —
                                                // covers "By appointment",
                                                // "Call to book", etc.
  const to24 = (h, ap) => {
    h = parseInt(h, 10);
    if (ap.toLowerCase() === "pm" && h !== 12) h += 12;
    if (ap.toLowerCase() === "am" && h === 12) h = 0;
    return h;
  };
  const now = new Date();
  const hr = now.getHours() + now.getMinutes() / 60;
  const open = to24(m[1], m[2]);
  const close = to24(m[3], m[4]);
  if (close <= open) return hr >= open || hr < close;
  return hr >= open && hr < close;
};

const todayHours = (park) => {
  const keys = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
  const k = keys[new Date().getDay()];
  return park.hours?.[k];
};

const ParkCard = ({ park, distance, active, onHover, onClick }) => {
  const open = isOpenNow(todayHours(park));
  const heroPhoto = park.photos?.[0]?.ref;
  // Delivery-only listings replace the "Open now / Closed" pill (which
  // is meaningless without walk-in hours) with a "Delivery only" chip.
  // Contact channels and everything else render normally. See
  // models/parks.py::Park.visit_type for the spec.
  const isDeliveryOnly = park.visit_type === "delivery_only";
  // All detail-URL routing flows through the single placeUrl() helper.
  // Community places → /place/{slug}; Google places with a slug and
  // resolvable city → /{cat}/{city}/{slug}; everyone else falls back
  // to /park/{id}. See lib/placeUrl.js for the full precedence.
  const isCommunity = park.source === "community";
  const isSponsored = park.is_sponsored === true;
  const detailHref = placeUrl(park);
  // Pinned sponsored cards fire a "pinned" attribution ping on any
  // click that leads to the internal detail page. Fire-and-forget so
  // it never blocks the navigation, and never surfaces errors — we'd
  // rather miss a ping than delay the user.
  const trackPinnedClick = () => {
    if (!isSponsored || !park.sponsor_slug) return;
    try {
      api.post(`/sponsors/${park.sponsor_slug}/click`, {
        referrer_path: window.location.pathname,
        click_source: "pinned",
      });
    } catch (_) { /* ignore — attribution is best-effort */ }
  };
  return (
    <Link
      to={detailHref}
      data-testid={`park-card-${park.id}`}
      onMouseEnter={() => onHover?.(park.id)}
      onMouseLeave={() => onHover?.(null)}
      onClick={() => { trackPinnedClick(); onClick?.(park.id); }}
      className={`block bg-white rounded-2xl shadow-warm hover:shadow-warm-lg transition-all border overflow-hidden ${
        active
          ? "border-brand-forest ring-2 ring-brand-forest/15"
          : isSponsored
          ? "border-[#E8D9C4]"
          : "border-brand-border-warm"
      }`}
    >
      {isSponsored && (
        // Yelp-style header strip — full-width peach band above the
        // card body so paid placements are scannable at a glance,
        // not a small pill inside the chip row. `title` on the info
        // icon gives a native tooltip disclosing which partner paid
        // for this slot (no library needed, accessible by default).
        <div
          data-testid={`park-sponsored-${park.id}`}
          className="flex items-center justify-between px-4 py-1.5"
          style={{ backgroundColor: "#FBEEE4", borderBottom: "1px solid #E8D9C4" }}
        >
          <span
            className="text-[11px] font-bold uppercase"
            style={{ color: "#B85C38", letterSpacing: "0.12em" }}
          >
            Sponsored
          </span>
          <span
            title={
              park.sponsor_name
                ? `Paid placement · ${park.sponsor_name}`
                : "Paid placement"
            }
            aria-label={
              park.sponsor_name
                ? `Paid placement by ${park.sponsor_name}`
                : "Paid placement disclosure"
            }
            className="inline-flex"
          >
            <Info className="w-3.5 h-3.5" style={{ color: "#B85C38" }} />
          </span>
        </div>
      )}
      <div className="flex gap-3 p-3 sm:p-4">
        {heroPhoto ? (
          <img
            src={photoUrl(heroPhoto, 240)}
            alt={park.name}
            loading="lazy"
            decoding="async"
            onLoad={() => markPhotoLoaded(heroPhoto)}
            className="w-24 h-24 sm:w-28 sm:h-28 rounded-xl object-cover bg-brand-light-green shrink-0"
          />
        ) : (
          <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-xl bg-brand-light-green flex items-center justify-center shrink-0">
            <Dog className="w-8 h-8 text-brand-forest" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <h3 className="font-heading text-lg sm:text-xl font-semibold text-brand-ink leading-tight truncate">
                {park.name}
              </h3>
              <div className="flex items-center gap-1.5 mt-1 text-xs text-brand-ink/60">
                <MapPin className="w-3.5 h-3.5 shrink-0" />
                <span className="truncate">
                  {isDeliveryOnly
                    // Delivery-only cards hide the unverified street
                    // address (see models/parks.py::visit_type) and
                    // fall back to city, state. Matches the same
                    // treatment on the detail page.
                    ? [park.city, park.state].filter(Boolean).join(", ")
                    : park.address}
                </span>
              </div>
            </div>
            <div className="flex flex-col items-end gap-1 shrink-0">
              {isCommunity ? (
                // Skip the star rating block on community places (rating
                // is always 0 since they're brand new) — and instead
                // surface a small "Just added" feel via the badge below.
                null
              ) : (
                <div className="flex items-center gap-1 text-sm font-semibold">
                  <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                  <span className="text-brand-ink">{park.rating?.toFixed(1)}</span>
                  {park.reviews_count > 0 && (
                    <span className="text-brand-ink/50 text-xs">({park.reviews_count})</span>
                  )}
                </div>
              )}
              {typeof distance === "number" && (
                <span className="text-xs text-brand-ink/60">{distance.toFixed(1)} mi</span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-1.5 mt-2 flex-wrap">
            {(() => {
              // On sponsored/pinned cards, honour `sponsor_category`
              // (the CURRENT page's category) so the chip reads "Pet
              // Sitters" on /pet-sitters instead of the underlying
              // doc's actual place_type (e.g. "Boarding"). Falls
              // through to `place_type` for organic cards.
              const catKey = (isSponsored && park.sponsor_category)
                ? park.sponsor_category
                : (park.place_type || "park");
              const cat = getCategory(catKey);
              const Icon = cat.icon;
              return (
                <span
                  data-testid={`park-category-${park.id}`}
                  className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-white px-2 py-0.5 rounded-full"
                  style={{ backgroundColor: cat.color }}
                >
                  {Icon && <Icon className="w-3 h-3" strokeWidth={2.6} />}
                  {cat.short}
                </span>
              );
            })()}
            {isCommunity && <CommunityBadge />}
            {isDeliveryOnly ? (
              <span
                data-testid={`park-delivery-only-${park.id}`}
                className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-brand-light-green text-brand-forest"
              >
                Delivery only
              </span>
            ) : open === null ? (
              // Hours unknown/unparseable — render no pill rather than
              // defaulting to "Open now" (9/6/2026 fix). Applies to
              // service-area businesses with no walk-in hours and to
              // listings whose hours are "By appointment"-style strings.
              null
            ) : (
              <span
                className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                  open ? "bg-brand-light-green text-brand-forest" : "bg-red-50 text-red-600"
                }`}
                data-testid={`park-status-${park.id}`}
              >
                {open ? "Open now" : "Closed"}
              </span>
            )}
            {(() => {
              const checkedIn = park.active_checkins || 0;
              const level = park.busy_level;
              if (checkedIn === 0 && !level) return null;
              const levelLabel = level && level[0].toUpperCase() + level.slice(1);
              return (
                <span
                  data-testid={`park-checkins-${park.id}`}
                  className="flex items-center gap-1 text-[11px] font-medium text-brand-forest bg-brand-light-green px-2 py-0.5 rounded-full"
                  title={
                    level
                      ? `Reported as ${levelLabel}${checkedIn ? ` · ${checkedIn} checked in` : ""}`
                      : `${checkedIn} dog${checkedIn === 1 ? "" : "s"} checked in`
                  }
                >
                  <Users className="w-3 h-3" />
                  {checkedIn > 0
                    ? `${checkedIn} dog${checkedIn === 1 ? "" : "s"} here`
                    : `Reported ${levelLabel}`}
                </span>
              );
            })()}
          </div>

          <div className="flex flex-wrap gap-1 mt-2">
            {isSponsored && park.sponsor_tags && park.sponsor_tags.length > 0 ? (
              // Sponsored cards render the partner's declared service
              // tags (from `db.sponsored_listings.tags`) instead of
              // the organic amenity chips — matches the "Pet Sitting
              // · Dog Walking · Pet Transport" surface that the top
              // Sponsored Results band shows, so both surfaces read
              // consistently for the same partner.
              park.sponsor_tags.map((t) => (
                <span
                  key={t}
                  data-testid={`park-sponsor-tag-${park.id}-${t.toLowerCase().replace(/\s+/g, "-")}`}
                  className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-brand-forest bg-brand-light-green px-2 py-0.5 rounded-full"
                >
                  {t}
                </span>
              ))
            ) : (
              park.amenities.map((a) => {
                const Icon = AMENITY_ICON[a] || Dog;
                return (
                  <span
                    key={a}
                    className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-brand-forest bg-brand-light-green px-2 py-0.5 rounded-full"
                  >
                    <Icon className="w-3 h-3" />
                    {AMENITY_LABEL[a] || a}
                  </span>
                );
              })
            )}
          </div>
        </div>
      </div>
    </Link>
  );
};

export default ParkCard;
export { isOpenNow, todayHours };
